import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { loginCall, getServerUrl, setServerUrl, testServerConnection } from '../api/client';
import styles from './LoginPage.module.css';

export default function LoginPage() {
  const remembered = useAuthStore((s) => s.rememberedUsername);
  const [username, setUsername] = useState(remembered || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(!!remembered);
  const [showConfig, setShowConfig] = useState(false);
  const [serverAddr, setServerAddr] = useState(getServerUrl());
  const [serverStatus, setServerStatus] = useState<'checking' | 'ok' | 'error'>('checking');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [testingServer, setTestingServer] = useState(false);
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();

  // Auto-detect server on mount
  useEffect(() => {
    checkServer();
  }, []);

  async function checkServer(url?: string) {
    setServerStatus('checking');
    const reachable = await testServerConnection(url);
    if (reachable) {
      setServerStatus('ok');
      setShowConfig(false);
    } else {
      setServerStatus('error');
      setShowConfig(true); // Auto-show config if server unreachable
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await loginCall(username, password);
      if (res.s === 'ok' && res.data?.[0]) {
        login(res.data[0].acctoken, res.data[0].displayName, res.data[0].userId, rememberMe, username);
        navigate('/worklist', { replace: true });
      } else {
        setError(res.error || 'Please check your credentials and try again.');
      }
    } catch {
      setError('Unable to reach the server. Please check your connection.');
      setShowConfig(true);
    } finally {
      setLoading(false);
    }
  }

  async function saveAndTestServer() {
    const url = serverAddr.replace(/\/$/, '');
    setServerUrl(url);
    setTestingServer(true);
    setError('');
    await checkServer(url);
    setTestingServer(false);
  }

  return (
    <div className={styles.container}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <img src={`${import.meta.env.BASE_URL}logo.jpg`} alt="EDGE PACS Viewer" className={styles.logo} />
        <div className={styles.version}>v{__APP_VERSION__}</div>

        {serverStatus === 'checking' && (
          <div className={styles.statusMsg}>Connecting to server...</div>
        )}

        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <div className={styles.optionsRow}>
          <label className={styles.rememberMe}>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            Remember me
          </label>
          <button
            type="button"
            className={`${styles.configBtn} ${serverStatus === 'error' ? styles.configBtnError : ''}`}
            onClick={() => setShowConfig(!showConfig)}
            title="Server settings"
          >
            {serverStatus === 'ok' ? '🟢' : serverStatus === 'error' ? '🔴' : '⏳'}
          </button>
        </div>
        {showConfig && (
          <div className={styles.configPanel}>
            <label className={styles.configLabel}>Server Address</label>
            <div className={styles.configRow}>
              <input
                type="text"
                placeholder="http://192.168.68.107:5677"
                value={serverAddr}
                onChange={(e) => setServerAddr(e.target.value)}
                className={styles.configInput}
              />
              <button
                type="button"
                onClick={saveAndTestServer}
                className={styles.configSaveBtn}
                disabled={testingServer}
              >
                {testingServer ? 'Testing...' : 'Save & Test'}
              </button>
            </div>
            {serverStatus === 'error' && (
              <div className={styles.configHint}>
                Cannot reach the server. Please check the address and ensure the service is running.
              </div>
            )}
            {serverStatus === 'ok' && (
              <div className={styles.configOk}>Connected</div>
            )}
          </div>
        )}
        {error && <div className={styles.error}>{error}</div>}
        <button type="submit" disabled={loading || serverStatus === 'checking'}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
    </div>
  );
}
